import React, { ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { TabPaneProps } from "antd";
import Tabs, { TabsProps } from "antd/es/tabs";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";

import DnDChild from "./DnDChild";
import styles from "./DnDTabs.module.scss";

interface Props extends Omit<TabsProps, "onDragEnd"> {
  defaultTabsOrder: string[];
  onDragEnd?: (tabsOrder: string[]) => void;
}

const DnDTabs: React.FC<Props> = ({
  activeKey,
  children,
  defaultTabsOrder,
  onChange,
  onDragEnd,
  ...props
}) => {
  const [tabsOrder, setTabsOrder] = useState(defaultTabsOrder || []);

  const childrenKeys = useMemo(
    () => React.Children.map(children, (c) => (c as ReactElement).key) || [],
    [children]
  );

  const sortedChildren: ReactElement<TabPaneProps>[] = useMemo(() => {
    // prepare array for sorted children
    const sortedChildren: ReactElement[] = Array.from({ length: React.Children.count(children) });
    // calculate their new indices and store in a Map<old_index, new_index>
    let undefinedOrderIndex = tabsOrder.length;
    const indexMap = new Map(
      childrenKeys.map((key, index) => [
        index,
        tabsOrder.includes(key) ? tabsOrder.indexOf(key) : undefinedOrderIndex++,
      ])
    );
    // put children into sortedChildren array according to their new indices
    React.Children.forEach(children, (child, index) => {
      sortedChildren[indexMap.get(index)!] = child as ReactElement<TabPaneProps>;
    });
    return sortedChildren;
  }, [children, childrenKeys, tabsOrder]);

  // select first tab when active tab does not exist or set
  useEffect(() => {
    const firstTabKey = sortedChildren[0]?.key;
    if (
      firstTabKey &&
      onChange &&
      (!activeKey || !sortedChildren.some((child: ReactElement) => child.key === activeKey))
    ) {
      onChange(firstTabKey);
    }
  }, [activeKey, onChange, sortedChildren]);

  const handleDragEnd = useCallback(
    ({ destination, source }: DropResult) => {
      const newTabsOrder = tabsOrder.slice();
      childrenKeys.forEach((key) => {
        if (key !== null && newTabsOrder.indexOf(key) === -1) {
          newTabsOrder.push(key);
        }
      });

      const dragKey = newTabsOrder[source.index];
      newTabsOrder.splice(source.index, 1);
      if (destination) {
        newTabsOrder.splice(destination.index, 0, dragKey);
      }

      setTabsOrder(newTabsOrder);
      onDragEnd?.(newTabsOrder);
    },
    [onDragEnd, childrenKeys, tabsOrder]
  );

  const renderTabBar = useCallback(
    (tabBarProps, DefaultTabBar) => {
      return (
        <DefaultTabBar {...tabBarProps}>
          {(node: ReactElement) => {
            const dndIndex = node.key ? tabsOrder.indexOf(node.key) : -1;
            return (
              <Draggable draggableId={`${node.key || ""}`} index={dndIndex} key={node.key}>
                {(provided, snapshot) => (
                  <DnDChild node={node} provided={provided} snapshot={snapshot} />
                )}
              </Draggable>
            );
          }}
        </DefaultTabBar>
      );
    },
    [tabsOrder]
  );

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable direction="horizontal" droppableId="droppableId">
        {(provided) => (
          <div className={styles.container} ref={provided.innerRef} {...provided.droppableProps}>
            <Tabs {...props} activeKey={activeKey} onChange={onChange} renderTabBar={renderTabBar}>
              {sortedChildren}
            </Tabs>
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DnDTabs;
